import { useContext, useEffect, useState } from "react";
import { useLocation } from "react-router";
import { IMainNavigation } from "../interfaces/navigation.interface";
import { AuthContext } from "../context/auth";
import { RightSideBarContext, RightSidebarActions } from "../context/rightSideBar";
import { getUserNavigation } from "../../core/services/routes/index";
import SideBarItem from "../components/SideBarItem";
import Header from "../components/TopNavBar";
import Loader from "../components/Loader";
import AppStoreIssueBanner from "../components/AppStoreIssueBanner";
import PausedBanner from "../components/PausedBanner";
import ImpersonationBanner from "../components/ImpersonationBanner";
import SandboxBanner from "../components/SandboxBanner";
import AnnouncementModal from "../components/AnnouncementModal";
import { useImpersonationSession } from "../hooks/useImpersonationSession";
import { useThemeContext } from "../context/theme";
import { getPlatformLockStatus } from '../services/organization-user/organization.service';

/**
 * Whether a sidebar entry should render as the current one.
 *
 * Matches on whole leading segments so a nested page keeps its parent
 * highlighted — /punchcards/create must keep Punchcards lit, and admin paths
 * are two segments deep (/admin/organizations), so comparing only the first
 * segment never matched them.
 *
 * Prefix matching alone is wrong when the nested page IS its own sidebar entry:
 * /settings/delivery would leave Settings lit alongside Mock Delivery. So a
 * matching entry with a longer path takes the highlight from its ancestor.
 *
 * Exported for test: SidebarLayout itself needs auth, redux, theme and router
 * context to render, and this is the whole of the rule.
 */
export const isNavPathCurrent = (
    itemPath: string | undefined,
    pathname: string | undefined,
    navPaths: string[]
): boolean => {
    if (!itemPath || !pathname) return false;

    const current = pathname.split("/").filter(Boolean);
    const segments = (path: string) => path.split("/").filter(Boolean);

    const matches = (path: string) => {
        const target = segments(path);
        if (!target.length) return false;
        return target.every((segment, idx) => current[idx] === segment);
    };

    if (!matches(itemPath)) return false;

    const depth = segments(itemPath).length;

    return !navPaths.some(
        (path) =>
            path !== itemPath && segments(path).length > depth && matches(path)
    );
};

const SidebarLayout = ({ children }: { children: React.ReactNode }) => {
    const location = useLocation();
    const { authState } = useContext(AuthContext);
    const { state: rightSideBarState, dispatch } = useContext(RightSideBarContext);
    const { isSidebarOpen, setIsSidebarOpen, theme } = useThemeContext();
    // While impersonating, or while the org is in sandbox mode, a strip is
    // added above the 64px header, so the fixed sidebar must start lower
    // (banner(s) + header). Each banner is the same height, so the two can
    // stack independently of each other.
    const isImpersonating = Boolean(useImpersonationSession());
    const isSandbox = Boolean(
        authState?.user?.relationships?.organization?.attributes?.is_sandbox
    );
    const bannerCount = Number(isImpersonating) + Number(isSandbox);
    const sidebarTopClass =
        bannerCount === 2
            ? "top-[176px]"
            : bannerCount === 1
            ? "top-[120px]"
            : "top-[64px]";
    const [navigation, setNavigation] = useState<IMainNavigation>({
        primary: [],
        secondary: [],
    });
    const [platformLocked, setPlatformLocked] = useState(false);
    const [isStaffUser, setIsStaffUser] = useState(false);

    useEffect(() => {
        if (rightSideBarState?.refresh) {
            document.getElementById("rightSideDrawer")?.click();
        }
    }, [rightSideBarState?.refresh]);

    useEffect(() => {
        if (!authState.user) {
            setIsStaffUser(false);
            setPlatformLocked(false);
            return;
        }

        const userType = authState.user.attributes?.type;
        // Only fetch platform lock for tenant users — admin users have no tenant context
        if (userType !== 'user') {
            setIsStaffUser(false);
            setPlatformLocked(false);
            return;
        }

        const email = authState.user.attributes?.email ?? '';
        const isStaff = email.startsWith('support+') &&
            email.endsWith('@digitalawesomeapps.com');
        setIsStaffUser(isStaff);

        if (!isStaff) {
            getPlatformLockStatus().then((locked: boolean) => {
                setPlatformLocked(locked);
            });
        } else {
            setPlatformLocked(false);
        }
    }, [authState.user]);

    useEffect(() => {
        if (!authState.loading && authState && authState.user) {
            const { user } = authState;
            const organizationType =
                user.relationships.organization?.attributes.type;
            const userType = user.relationships.role.attributes.type;
            const roleName = user.relationships.role.attributes.name;
            if (
                authState?.user?.relationships?.role?.attributes?.type ===
                "user" &&
                !authState.user
            ) {
                return;
            }

            setNavigation(
                getUserNavigation(
                    organizationType,
                    userType,
                    roleName,
                    authState?.user?.relationships?.organization?.relationships
                        ?.features?.attributes,
                    authState?.user?.relationships?.organization?.attributes
                        ?.mixpanel_project_url,
                    authState?.user?.relationships?.role,
                    platformLocked,
                    isStaffUser || isImpersonating,
                    isSandbox,
                ) as any
            );
        }
    }, [location?.pathname, authState.loading, authState.user, platformLocked, isStaffUser, isImpersonating]);

    const toggleSidebar = () => {
        setIsSidebarOpen(!isSidebarOpen);
    };

    // Below lg the sidebar is an overlay, so leaving the page must dismiss it.
    useEffect(() => {
        setIsSidebarOpen(false);
    }, [location?.pathname]);

    useEffect(() => {
        if (!isSidebarOpen) return;
        const onKeyDown = (e: KeyboardEvent) => {
            if (e.key === "Escape") setIsSidebarOpen(false);
        };
        document.addEventListener("keydown", onKeyDown);
        return () => document.removeEventListener("keydown", onKeyDown);
    }, [isSidebarOpen]);

    const isNavItemCurrent = (itemPath?: string) =>
        isNavPathCurrent(
            itemPath,
            location?.pathname,
            [...navigation.primary, ...navigation.secondary]
                .map((item) => item.path)
                .filter((path): path is string => Boolean(path))
        );

    return (
        // One viewport-tall column so only #main-content scrolls. daisyUI pins
        // each .drawer to 100dvh with a scrollable .drawer-content, which
        // otherwise gives the page, the drawer and the section a scrollbar each.
        <div className="flex h-[100dvh] flex-col overflow-hidden">
            {/* One unit so the two banners stack in normal flow instead of
                overlapping when both are shown (e.g. a super admin
                impersonating a sandbox org). */}
            <div className="relative z-50 shrink-0">
                <ImpersonationBanner />
                <SandboxBanner />
            </div>
            {/* One mount covers every organization route. Self-gated to tenant
                users, so the admin portal renders and fetches nothing. */}
            <AnnouncementModal />
            <div className="drawer drawer-end !h-auto min-h-0 flex-1">
                <input
                    id="rightSideDrawer"
                    type="checkbox"
                    className="drawer-toggle"
                />
                <div className="drawer-content !overflow-y-hidden">
                    <div className="drawer drawer-mobile !h-full">
                        <input
                            id="leftSideDrawer"
                            type="checkbox"
                            className="drawer-toggle"
                        />
                        <div className="drawer-content lg:!z-[1000] flex flex-col !overflow-y-hidden">
                            <div className="shrink-0">
                                <Header toggleSidebar={toggleSidebar} />
                            </div>
                            <div className="flex min-h-0 flex-1">
                                {isSidebarOpen && (
                                    <div
                                        className={`fixed inset-x-0 bottom-0 ${sidebarTopClass} z-30 bg-black/40 lg:hidden`}
                                        onClick={toggleSidebar}
                                        data-testid="sidebar-backdrop"
                                    />
                                )}
                                <div
                                    className={`drawer-side border-r bg-dark-gray fixed left-0 ${sidebarTopClass} bottom-0 z-40 w-[288px] max-w-[85vw] transition-transform duration-300 lg:translate-x-0 ${isSidebarOpen
                                            ? "translate-x-0"
                                            : "-translate-x-full"
                                        }`}
                                >
                                    <nav className="space-y-2 p-2 md:py-6 md:px-6 h-full overflow-y-auto text-base-content">
                                        <ul>
                                            {navigation.primary.map(
                                                (item, idx) => (
                                                    <SideBarItem
                                                        icon={item.icon as any}
                                                        name={item?.name}
                                                        iconActive={
                                                            item.iconActive
                                                        }
                                                        path={item?.path}
                                                        subItems={item.subItems}
                                                        key={idx}
                                                        isExpanded
                                                        isCurrent={!item.subItems && isNavItemCurrent(item?.path)}
                                                        isExternal={
                                                            item.isExternal
                                                        }
                                                        disabled={item.disabled ?? false}
                                                    />
                                                )
                                            )}
                                        </ul>
                                        <hr />
                                        <ul>
                                            {navigation.secondary.map(
                                                (item, idx) => (
                                                    <SideBarItem
                                                        icon={item.icon}
                                                        name={item?.name}
                                                        iconActive={
                                                            item.iconActive
                                                        }
                                                        path={item?.path}
                                                        subItems={item.subItems}
                                                        key={idx}
                                                        isExpanded
                                                        isCurrent={!item.subItems && isNavItemCurrent(item?.path)}
                                                        isExternal={
                                                            item.isExternal
                                                        }
                                                        disabled={item.disabled ?? false}
                                                    />
                                                )
                                            )}
                                        </ul>
                                    </nav>
                                </div>
                                <section
                                    className="flex-1 min-w-0 overflow-y-auto lg:pl-[288px]"
                                    style={theme === 'light' ? { backgroundColor: '#F9FAFB' } : undefined}
                                    id="main-content"
                                >
                                    <AppStoreIssueBanner />
                                    <PausedBanner />
                                    {authState.loading ? (
                                        <div className="flex justify-center h-full">
                                            <Loader />
                                        </div>
                                    ) : (
                                        <div className="px-1 py-2 lg:py-6 lg:px-6">
                                            {children}
                                        </div>
                                    )}
                                </section>
                            </div>
                        </div>
                    </div>
                </div>
                <div className="drawer-side">
                    <label
                        className="drawer-overlay"
                        onClick={() => {
                            // Uncheck the drawer first, then clear content: clearing
                            // first unmounts before the drawer closes, snapping the
                            // width/blanking the panel mid-dismiss.
                            const drawer = document.getElementById(
                                "rightSideDrawer"
                            ) as HTMLInputElement | null;
                            if (drawer) drawer.checked = false;
                            dispatch({ action: RightSidebarActions.REMOVEITEM });
                        }}
                    />
                    <div className="p-4 sm:p-6 lg:p-10 overflow-y-auto bg-base-100 text-base-content w-[90vw] sm:w-4/5 lg:w-1/4">
                        {rightSideBarState?.component}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default SidebarLayout;
